import { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Check } from '@mui/icons-material'
import { Button, Chip, FormControl, InputLabel, List, ListItem, ListItemText, MenuItem, Select, Stack, Typography } from '@mui/material'

import { useGetInternalCategoriesQuery } from '../../../../api/categories'
import { useGetInternalCategoryMarketFeaturesQuery } from '../../../../api/feature-bulk-assign'
import { InternalFormDialog } from '../../../../components/InternalFormDialog/InternalFormDialog'
import { QueryStateBoundary } from '../../../../components/QueryStateBoundary/QueryStateBoundary'
import { BulkAssignFeature } from '../../types/BulkAssign'
import { maxBenchmarkFeatures, toBenchmarkFeature, toggleBenchmarkFeature } from '../helpers/bulkAssignHelpers'

type BenchmarkFeatureDialogProps = {
  open: boolean
  marketIso: string
  excludedLegacyId: number
  selected: BulkAssignFeature[]
  onClose: () => void
  onDone: (features: BulkAssignFeature[]) => void
}

export const BenchmarkFeatureDialog: FC<BenchmarkFeatureDialogProps> = ({ open, marketIso, excludedLegacyId, selected, onClose, onDone }) => {
  const { t } = useTranslation()
  const [picked, setPicked] = useState<BulkAssignFeature[]>([])
  const [categoryId, setCategoryId] = useState('')
  const categoriesQuery = useGetInternalCategoriesQuery({ marketIso }, { skip: !open || !marketIso })
  const categories = categoriesQuery.data
  const currentCategoryId = categoryId || categories?.[0]?.id || ''
  const featuresQuery = useGetInternalCategoryMarketFeaturesQuery(
    { categoryId: currentCategoryId, marketIso },
    { skip: !open || !currentCategoryId || !marketIso }
  )
  const features = useMemo(
    () => (featuresQuery.data ?? []).filter((feature) => feature.legacyId !== excludedLegacyId).map(toBenchmarkFeature),
    [featuresQuery.data, excludedLegacyId]
  )

  useEffect(() => {
    if (open) setPicked(selected)
  }, [open, selected])

  const isPicked = (feature: BulkAssignFeature) => picked.some((candidate) => candidate.legacyId === feature.legacyId)
  const isFull = picked.length >= maxBenchmarkFeatures

  return (
    <InternalFormDialog
      open={open}
      title={t('internal-analysts:bulk_assign_benchmark_title')}
      onClose={onClose}
      onSave={() => onDone(picked)}
      saveLabel={t('internal-analysts:bulk_assign_benchmark_done')}
    >
      <Stack spacing={2} pt={1}>
        <Typography variant="body2" color={isFull ? 'warning.main' : 'text.secondary'}>
          {t('internal-analysts:bulk_assign_benchmark_limit', { count: maxBenchmarkFeatures })}
        </Typography>
        {picked.length > 0 && (
          <Stack direction="row" gap={1} flexWrap="wrap">
            {picked.map((feature) => (
              <Chip key={feature.legacyId} label={feature.name} onDelete={() => setPicked((current) => toggleBenchmarkFeature(current, feature))} />
            ))}
          </Stack>
        )}
        <QueryStateBoundary
          isLoading={categoriesQuery.isLoading}
          isError={categoriesQuery.isError}
          errorText={t('internal-analysts:bulk_assign_benchmark_load_failed')}
          skeletonHeight={56}
        >
          <FormControl fullWidth>
            <InputLabel id="benchmark-category">{t('internal-analysts:bulk_assign_benchmark_category')}</InputLabel>
            <Select
              labelId="benchmark-category"
              label={t('internal-analysts:bulk_assign_benchmark_category')}
              value={currentCategoryId}
              onChange={(event) => setCategoryId(event.target.value)}
            >
              {(categories ?? []).map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </QueryStateBoundary>
        <QueryStateBoundary
          isLoading={featuresQuery.isFetching}
          isError={featuresQuery.isError}
          errorText={t('internal-analysts:bulk_assign_benchmark_load_failed')}
          skeletonHeight={160}
        >
          {!features.length ? (
            <Typography color="text.secondary">{t('internal-analysts:bulk_assign_benchmark_no_features')}</Typography>
          ) : (
            <List dense disablePadding>
              {features.map((feature) => {
                const active = isPicked(feature)
                return (
                  <ListItem
                    key={feature.legacyId}
                    divider
                    secondaryAction={
                      <Button
                        size="small"
                        variant={active ? 'contained' : 'outlined'}
                        startIcon={active ? <Check /> : undefined}
                        disabled={!active && isFull}
                        aria-pressed={active}
                        aria-label={t('internal-analysts:bulk_assign_benchmark_select_named', { name: feature.name })}
                        onClick={() => setPicked((current) => toggleBenchmarkFeature(current, feature))}
                      >
                        {active ? t('internal-analysts:bulk_assign_benchmark_selected') : t('internal-analysts:bulk_assign_benchmark_select')}
                      </Button>
                    }
                  >
                    <ListItemText primary={feature.name} secondary={t('internal-analysts:bulk_assign_feature_id', { id: feature.legacyId })} />
                  </ListItem>
                )
              })}
            </List>
          )}
        </QueryStateBoundary>
      </Stack>
    </InternalFormDialog>
  )
}
